<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES, ATTACHMENT_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const MEDIA_TYPES = [ATTACHMENT_TYPES.IMAGE, ATTACHMENT_TYPES.VIDEO];

const mediaOnlyAttachments = computed(() => {
  const all = Array.isArray(attachments.value) ? attachments.value : [];
  return all.filter(a => MEDIA_TYPES.includes(a.fileType));
});

const nonMediaAttachments = computed(() => {
  const all = Array.isArray(attachments.value) ? attachments.value : [];
  return all.filter(a => !MEDIA_TYPES.includes(a.fileType));
});

const hasMediaAttachments = computed(() => mediaOnlyAttachments.value.length > 0);

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="overflow-hidden" :class="hasMediaAttachments ? 'p-0' : 'px-4 py-3'" data-bubble-name="text">
    <div class="gap-0 flex flex-col">
      <AttachmentChips :attachments="mediaOnlyAttachments" class="gap-0" is-inline />
      <div class="flex flex-col gap-3 px-4 py-3" v-if="renderContent || isEmpty || hasTranslations || nonMediaAttachments.length || isTemplate">
        <span v-if="isEmpty" class="text-n-slate-11">
          {{ $t('CONVERSATION.NO_CONTENT') }}
        </span>
        <FormattedContent v-if="renderContent" :content="renderContent" />
        <TranslationToggle
          v-if="hasTranslations"
          class="-mt-3"
          :showing-original="renderOriginal"
          @toggle="handleSeeOriginal"
        />
        <AttachmentChips :attachments="nonMediaAttachments" class="gap-2" />
        <template v-if="isTemplate">
          <div
            v-if="contentAttributes.submittedEmail"
            class="px-2 py-1 rounded-lg bg-n-alpha-3"
          >
            {{ contentAttributes.submittedEmail }}
          </div>
        </template>
      </div>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
